<template>
    <app-layout title="Reports">
        <page-header>
            <template #title>Reports - Schools</template>

            <template #actions>
                <jet-tertiary-button type="button" class="py-1" @click="handlePrint" :disabled="form.processing || !canRunReport || !report_data.length" >
                    <img src="/images/icons/print.svg" width="16" height="16" alt="Print">
                </jet-tertiary-button>

                <jet-button type="button" @click="handleSubmit" :disabled="form.processing || !canRunReport" >
                    Run Report
                </jet-button>
            </template>
        </page-header>

        <reports-navigation />

        <div class="px-8">
            <div class="flex">
                <form class="flex flex-1 gap-x-2 gap-y-2 flex-wrap" @submit.prevent="handleSubmit">
                    <div class="flex w-full xl:w-1/2 gap-x-2">
                        <div class="flex flex-col flex-1">
                            <jet-label for="report" value="Report" class="whitespace-nowrap"/>
                            <multiselect class="multi-sm whitespace-nowrap h-7 text-base"
                                v-model="form.report"
                                :options="reportOptions"
                                valueProp="value"
                                trackBy="label"
                                label="label"
                                :canClear="false"
                                :canDeselect="false"
                                placeholder="Select a Report"
                                :classes="{singleLabel: 'w-full px-2 whitespace-nowrap'}"
                            />
                        </div>

                        <div class="flex flex-col flex-1">
                            <jet-label for="date_range" value="Date Range" />
                            <datepicker inputClassName="dp-custom-input"
                                @update:modelValue="handleUpdateDateRange"
                                v-model="date"
                                :is24="false"
                                :enableTimePicker="false"
                                :autoApply="true"
                                range
                                twoCalendars
                                placeholder="Select Date Range"
                                :format="formatDateRange"
                                weekStart=0
                            />
                        </div>

                    </div>

                    <!-- Filters -->
                    <div class="flex flex-col flex-1">
                        <jet-label for="filters" value="Filters" />
                        <div v-if="form.report == null" class="text-gray-400">Select a report to view filters</div>
                        <div v-else class="flex flex-1 gap-x-2">

                            <div v-show="showFilterByFilter" class="w-40">
                                <multiselect class="multi-sm h-7 text-base"
                                    v-model="form.filter_by"
                                    :options="filterOptions"
                                    valueProp="value"
                                    trackBy="label"
                                    label="label"
                                    :canClear="false"
                                    :canDeselect="false"
                                    :classes="{singleLabel: 'w-full px-2'}"
                                />
                            </div>

                            <div v-show="showCategoryFilter" class="w-48">
                                <multiselect class="multi-custom h-7 text-base"
                                    v-model="form.category_id"
                                    :options="categories"
                                    valueProp="id"
                                    trackBy="slug"
                                    label="title"
                                    :searchable="true"
                                    placeholder="Category"
                                >
                                    <template v-slot:option="{ option }">
                                        <span :class="{ 'ml-5' : option.parent_id != null }">{{ option.title }} </span>
                                    </template>
                                </multiselect>
                            </div>

                            <div v-show="showPricingLevelFilter" class="w-60">
                                <multiselect class="multi-custom h-7 text-base"
                                             v-model="form.pricing_levels"
                                             :options="pricing_levels"
                                             mode="multiple"
                                             valueProp="value"
                                             trackBy="value"
                                             label="label"
                                             :searchable="true"
                                             placeholder="All Pricing Levels"
                                >
                                </multiselect>
                            </div>

                            <div v-show="form.report === 'item_sales'" class="w-60 relative">
                                <multiselect
                                    v-model="form.purchaser_id"
                                    :options="purchasers"
                                    valueProp="id"
                                    trackBy="id"
                                    label="name"
                                    placeholder="All Purchasers"
                                    mode="multiple"
                                    :closeOnSelect="false"
                                     class="multi-custom h-7 text-base"
                                    :classes="{multipleLabel: 'w-full px-2'}"
                                >
                                    <template v-slot:multiplelabel="{ values }">
                                        <div class="multiselect-multiple-label">
                                            {{ values.length }} purchaser{{ values.length > 1 ? 's' : ''}}
                                        </div>
                                    </template>
                                </multiselect>
                                <div v-show="form.purchaser_id && form.purchaser_id.length" class="absolute -bottom-6 w-full truncate">
                                    <span v-for="(single_purchaser_id, index) in form.purchaser_id" :key="single_purchaser_id" class="text-xs">
                                        {{ purchasers.find(p => p.id == single_purchaser_id).name }}{{index == form.purchaser_id.length - 1 ? '' : ', '}}
                                    </span>
                                </div>
                            </div>

                            <div v-show="showSalesRepFilter" class="w-48">
                                <multiselect class="multi-custom h-7 text-base"
                                    v-model="form.sales_id"
                                    :options="sales_reps"
                                    valueProp="id"
                                    trackBy="name"
                                    label="name"
                                    :classes="{singleLabel: 'w-full px-2'}"
                                />
                            </div>

                        </div>
                    </div>

                    <div class="xl:hidden self-end">
                        <jet-tertiary-button type="button" class="py-1 ml-4 h-7" @click="handleReset" >
                            Reset
                        </jet-tertiary-button>
                    </div>

                </form>

                <div class="hidden xl:block self-end">
                    <jet-tertiary-button type="button" class="py-1 ml-4 h-7" @click="handleReset" >
                        Reset
                    </jet-tertiary-button>
                </div>
            </div>
        </div>

        <div class="px-8 py-6">

            <div v-if="!form.processing" >
                <div v-if="form.report != null && report_data.length > 0">

                    <div v-if="!form.isDirty" class="rounded overflow-y-auto border report-container" style="max-height:70vh">
                        <!-- Report Vue Components -->
                        <item-sales v-if="form.report === 'item_sales' && !form.isDirty" @sort="handleSort" :data="report_data" :sort_column="sort_column" :sort_direction="sort_direction"/>
                        <customer-sales v-if="form.report === 'customer_sales' && !form.isDirty" @sort="handleSort" :data="report_data" :sort_column="sort_column" :sort_direction="sort_direction"/>
                        <rep-sales v-if="form.report === 'rep_sales' && !form.isDirty" @sort="handleSort" :data="report_data" :sort_column="sort_column" :sort_direction="sort_direction"/>
                    </div>

                    <div v-else class="text-center text-gray-400 text-2xl pt-10">
                        Click Run Report to refresh data
                    </div>

                </div>

                <div v-show="showNoReportDataMsg" class="text-center text-gray-400 text-2xl pt-10">
                    No data for selected report parameters
                </div>

                <div v-show="showInitialReportMsg" class="text-center text-gray-400 text-2xl pt-10">
                    Select report parameters & click Run Report button
                </div>
            </div>

            <div v-else class="text-center text-gray-400 text-2xl pt-10">
                Retrieving report data...
            </div>
        </div>

    </app-layout>
</template>

<script>
import { useForm } from '@inertiajs/inertia-vue3';
import { usePage } from '@inertiajs/inertia-vue3'
import UseUserAuth from '@/Composables/UseUserAuth';
import AppLayout from '@/Layouts/AppLayout.vue';
import PageHeader from '@/Layouts/partials/PageHeader.vue';
import ReportsNavigation from '@/Pages/Reports/ReportsNavigation';
import JetButton from '@/Jetstream/Button';
import JetInput from '@/Jetstream/Input';
import JetLabel from '@/Jetstream/Label';
import JetSecondaryButton from '@/Jetstream/SecondaryButton';
import JetTertiaryButton from '@/Jetstream/TertiaryButton';
import Multiselect from '@vueform/multiselect';
import Datepicker from '@vuepic/vue-datepicker';
import { DateTime } from "luxon";

// Reuse Wholesale report table components
import CustomerSales from '@/Pages/Reports/Wholesale/CustomerSales';
import ItemSales from '@/Pages/Reports/Wholesale/ItemSales';
import RepSales from '@/Pages/Reports/Wholesale/RepSales';

import '@vueform/multiselect/themes/default.css';
import '@vuepic/vue-datepicker/dist/main.css';

export default {
    setup(props) {

        const form = useForm({
            report: props.filters.report,
            filter_by: props.filters.filter_by ?? 'ship_date',
            category_id: props.filters.category_id,
            pricing_levels: props.filters.pricing_levels,
            sales_id: props.filters.sales_id ?? null,
            purchaser_id: props.filters.purchaser_id ?? [],
            from: props.filters.from,
            to: props.filters.to,
            sort_column: props.sort_column,
            sort_direction: props.sort_direction ?? 'asc',
            default_sort_column: 'id',
        });

        const { hasFinanceRole } = UseUserAuth(usePage().props.value.user);

        return {
            form,
            hasFinanceRole,
        }
    },

    components: {
        AppLayout,
        PageHeader,
        ReportsNavigation,
        JetButton,
        JetInput,
        JetLabel,
        JetSecondaryButton,
        JetTertiaryButton,
        Multiselect,
        Datepicker,
        CustomerSales,
        ItemSales,
        RepSales,
    },

    props: ['data', 'filters', 'sort_column', 'sort_direction', 'categories', 'pricing_levels', 'sales_reps', 'purchasers'],

    data() {
        return {
            report_data: this.data,

            date: null,

            filterOptions: [
                { value:'order_date', label:'Order Date' },
                { value:'ship_date', label:'Ship Date' },
            ],
        }
    },

    methods: {
        handleSubmit: _.debounce(function () {
            this.form.submit('post', route('reports.schools.report'), { preserveState: true });
        }, 250),

        handleSort (sort) {
            this.form.sort_column = sort.column;
            this.form.sort_direction = sort.direction;
            this.handleSubmit();
        },

        handleReset() {
            this.report_data = [];
            this.date = null;

            this.form.report = null;
            this.form.filter_by = 'ship_date';
            this.form.category_id = null;
            this.form.pricing_levels = null;
            this.form.sales_id = null;
            this.form.from = null;
            this.form.to = null;
            this.form.purchaser_id = [];
            this.form.sort_column = null;
            this.form.sort_direction = 'asc';
            this.handleSubmit();

        },

        handlePrint() {
            window.open((route('reports.schools.print', {
                report: this.form.report,
                filter_by: this.form.filter_by,
                category_id: this.form.category_id,
                pricing_levels: this.form.pricing_levels,
                sales_id: this.form.sales_id,
                from: this.form.from,
                to: this.form.to,
                sort_column: this.form.sort_column,
                sort_direction: this.form.sort_direction,
                purchaser_id: this.form.purchaser_id
            } )), "_blank");
        },

        setDateRange() {
            if(this.filters.from !== null && this.filters.to !== null) {
                this.date = [];
                this.date.push(this.formatDateToJS(this.filters.from));
                this.date.push(this.formatDateToJS(this.filters.to));
            }
        },

        handleUpdateDateRange(dateRange) {
            this.form.from = dateRange && dateRange.length ? DateTime.fromJSDate(dateRange[0]).toISODate() : null;
            this.form.to = dateRange && dateRange.length ? DateTime.fromJSDate(dateRange[1]).toISODate() : null;
        },

        formatDateToJS(date) {
            return DateTime.fromISO(date).toJSDate();
        },

        formatDateRange(dateRange) {
            const start = dateRange[0];
            const end = dateRange[1];

            if(start && end)
            {
                let thisYear = DateTime.now().toFormat('yyyy');
                let startYear = DateTime.fromJSDate(start).toFormat('yyyy');
                let endYear = DateTime.fromJSDate(end).toFormat('yyyy');
                let startDate = startYear === thisYear ? DateTime.fromJSDate(start).toFormat('LLL. d') : DateTime.fromJSDate(start).toFormat("LLL. d, yyyy");
                let endDate = endYear === thisYear ? DateTime.fromJSDate(end).toFormat('LLL. d') : DateTime.fromJSDate(end).toFormat("LLL. d, yyyy");

                return startDate + ' - ' + endDate;
            }

            return null;
        }

    },

    computed: {
        reportOptions() {
            let options = [{ value: 'item_sales', label: 'Item Sales' }];

            if(this.hasFinanceRole) {
                options.push(
                    { value: 'rep_sales', label: 'Sales by Rep'},
                    { value: 'customer_sales', label: 'Sales by Customer' },
                );
            }

            return options;
        },
        showFilterByFilter() {
            return this.form.report === 'item_sales' || this.form.report === 'customer_sales';
        },
        showCategoryFilter() {
            return this.form.report === 'item_sales';
        },
        showPricingLevelFilter() {
            return this.form.report === 'item_sales'
        },
        showSalesRepFilter() {
            return this.form.report === 'rep_sales' || this.form.report === 'customer_sales';
        },
        canRunReport() {
            const hasDateRange = this.form.from != null && this.form.to != null;
            const hasRep = this.form.report !== 'rep_sales' || this.form.sales_id;

            return this.form.report != null && hasDateRange && hasRep;
        },
        defaultSortColumn() {
            // Set the default sort column depending on the report selection
            if (this.form.report === 'item_sales') return 'title';
            else if (this.form.report === 'customer_sales') return 'name';
            else return 'id';   // Probably safe
        },
        showInitialReportMsg() {
            return this.form.report == null ||
                (this.form.report != null && this.form.isDirty && this.report_data.length == 0 );
        },
        showNoReportDataMsg() {
            return this.form.report != null && !this.form.isDirty && this.report_data.length == 0;
        },
    },

    watch: {
        'form.report': {
            immediate: true,
            deep: true,
            handler: function() {
                this.form.sort_column = this.defaultSortColumn;
                this.form.default_sort_column = this.defaultSortColumn;
            }
        },

        data: {
            immediate: true,
            handler (newValue) {
                this.report_data = newValue;
            }
        }
    },

    mounted() {
        this.setDateRange();
    }
}
</script>

<style scoped>
.multi-custom {
    min-height: 1.5rem;
}
</style>

<style>
.dp-custom-input {
    @apply h-7;
}

.report-container thead {
    position: sticky;
    top: 0;
}
.report-container tfoot {
    position: sticky;
    bottom: 0;
}
.report-container tfoot td {
    height: 1.5rem;
    line-height: 1.5rem;
    background-color: #efefef;
    @apply border-gray border-b-0 border-t-2;
    @apply font-bold text-sm;
    @apply pt-2 pb-1 px-2;
}
</style>
